import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  CalendarBlank, TrendUp, TrendDown, Minus,
  Plus, PencilSimple, Trash, MagnifyingGlass,
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BACKEND_URL } from '../config';

const FY_MONTH_ORDER = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

const formatMonth = (label) => {
  if (!label) return '';
  const [year, month] = label.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month) - 1]} ${year}`;
};

const MonthlyBreakdown = ({ canWrite, clients }) => {
  const [data, setData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const selectedMonthRef = useRef(selectedMonth);
  selectedMonthRef.current = selectedMonth;
  const [selectedFY, setSelectedFY] = useState('all');
  const [loading, setLoading] = useState(true);

  const [sales, setSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [addSaleOpen, setAddSaleOpen] = useState(false);
  const [editSale, setEditSale] = useState(null);
  const [deleteSale, setDeleteSale] = useState(null);
  const [formData, setFormData] = useState({ client_id: '', amount: '', description: '', date: '' });

  const fetchMonthly = useCallback(async () => {
    try {
      const params = {};
      if (selectedFY && selectedFY !== 'all') params.fy = selectedFY;
      const res = await axios.get(`${BACKEND_URL}/api/analytics/monthly`, { params, withCredentials: true });
      setData(res.data);
      if (!selectedMonthRef.current && res.data.months?.length > 0) {
        setSelectedMonth(res.data.months[res.data.months.length - 1].month);
      }
    } catch (err) {
      toast.error('Failed to load monthly data');
    } finally {
      setLoading(false);
    }
  }, [selectedFY]);

  const fetchSales = useCallback(async () => {
    if (!selectedMonth) return;
    setSalesLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/sales/by-month/${selectedMonth}`, { withCredentials: true });
      setSales(res.data);
    } catch (err) { console.error(err.message);
      toast.error('Failed to load sales');
    } finally {
      setSalesLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => { fetchMonthly(); }, [fetchMonthly]);
  useEffect(() => { fetchSales(); }, [fetchSales]);

  const handleFYChange = (val) => {
    setSelectedFY(val);
    setSelectedMonth(null);
  };

  const handleAddSale = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/sales`, {
        client_id: formData.client_id,
        amount: parseFloat(formData.amount),
        description: formData.description || null,
        date: formData.date || null,
      }, { withCredentials: true });
      toast.success('Sale added');
      setAddSaleOpen(false);
      setFormData({ client_id: '', amount: '', description: '', date: '' });
      fetchSales();
      fetchMonthly();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add sale');
    }
  };

  const handleEditSale = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${BACKEND_URL}/api/sales/${editSale.id}`, {
        amount: parseFloat(formData.amount),
        description: formData.description,
        date: formData.date,
        client_id: formData.client_id,
      }, { withCredentials: true });
      toast.success('Sale updated');
      setEditSale(null);
      fetchSales();
      fetchMonthly();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update sale');
    }
  };

  const handleDeleteSale = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/sales/${deleteSale.id}`, { withCredentials: true });
      toast.success('Sale deleted');
      setDeleteSale(null);
      fetchSales();
      fetchMonthly();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete sale');
    }
  };

  const openEdit = (sale) => {
    setFormData({
      client_id: sale.client_id,
      amount: String(sale.amount),
      description: sale.description || '',
      date: sale.date?.slice(0, 10) || '',
    });
    setEditSale(sale);
  };

  const openAdd = () => {
    const defaultDate = selectedMonth ? `${selectedMonth}-15` : '';
    setFormData({ client_id: '', amount: '', description: '', date: defaultDate });
    setAddSaleOpen(true);
  };

  const filteredSales = searchQuery.trim()
    ? sales.filter(s =>
        s.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sales;

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading monthly data...</div>;
  if (!data?.months?.length && !data?.financial_years?.length) return <div className="text-center py-12 text-muted-foreground">No monthly data available yet.</div>;

  const chartData = (data.months || []).map(m => ({
    month: formatMonth(m.month),
    revenue: m.total_revenue,
  }));

  const selected = (data.months || []).find(m => m.month === selectedMonth);
  const currentFYSummary = (data.fy_summaries || []).find(s => s.financial_year === selectedFY);

  return (
    <div className="space-y-6" data-testid="monthly-breakdown-section">
      {/* FY Selector + Summary */}
      <div className="bg-card border border-border rounded-sm p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <CalendarBlank size={24} weight="regular" />
            <div>
              <div className="text-xl font-semibold tracking-tight">Monthly Revenue</div>
              <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">GROUPED BY FINANCIAL YEAR (APR-MAR)</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedFY} onValueChange={handleFYChange} data-testid="fy-selector">
              <SelectTrigger className="w-[180px] rounded-sm" data-testid="fy-selector-trigger">
                <SelectValue placeholder="All Financial Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Financial Years</SelectItem>
                {(data.financial_years || []).map(fy => (
                  <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* FY Summary Cards */}
        {selectedFY === 'all' && data.fy_summaries?.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
            {data.fy_summaries.map(s => (
              <button
                key={s.financial_year}
                onClick={() => handleFYChange(s.financial_year)}
                className="p-4 rounded-sm border border-border bg-background hover:border-primary/50 text-left transition-all"
                data-testid={`fy-card-${s.financial_year}`}
              >
                <div className="text-xs tracking-[0.2em] uppercase text-primary font-bold">{s.financial_year}</div>
                <div className="text-lg font-bold mt-1">₹{s.total_revenue.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{s.total_transactions.toLocaleString()} transactions</div>
              </button>
            ))}
          </div>
        )}

        {/* Selected FY Summary Banner */}
        {selectedFY !== 'all' && currentFYSummary && (
          <div className="flex items-center gap-6 p-4 mb-4 rounded-sm bg-primary/5 border border-primary/20">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-primary">{currentFYSummary.financial_year}</div>
              <div className="text-2xl font-bold">₹{currentFYSummary.total_revenue.toLocaleString()}</div>
            </div>
            <div className="text-sm text-muted-foreground">
              {currentFYSummary.total_transactions.toLocaleString()} transactions across {(data.months || []).length} months
            </div>
          </div>
        )}

        {/* Bar Chart */}
        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} onClick={(e) => {
              if (e?.activeLabel) {
                const idx = chartData.findIndex(d => d.month === e.activeLabel);
                if (idx >= 0) setSelectedMonth(data.months[idx].month);
              }
            }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--foreground))" style={{ fontSize: '11px' }} />
              <YAxis stroke="hsl(var(--foreground))" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem' }}
                formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
              />
              <Bar dataKey="revenue" fill="hsl(var(--accent))" cursor="pointer" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Month Cards */}
      {(data.months || []).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {data.months.map(m => (
            <button
              key={m.month}
              onClick={() => setSelectedMonth(m.month)}
              data-testid={`month-card-${m.month}`}
              className={`p-4 rounded-sm border text-left transition-all ${
                selectedMonth === m.month
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-bold">{formatMonth(m.month)}</div>
              <div className="text-lg font-bold mt-1">₹{m.total_revenue.toLocaleString()}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground">{m.transactions} txns</span>
                {m.growth_percentage !== null && (
                  <span className={`text-xs font-semibold ${m.growth_percentage > 0 ? 'text-accent' : m.growth_percentage < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {m.growth_percentage > 0 ? '+' : ''}{m.growth_percentage}%
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Month Detail + Sales Table */}
      {selected && (
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-xl font-semibold tracking-tight">{formatMonth(selected.month)} Sales</div>
              <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">
                {selected.transactions} TRANSACTIONS | {selected.active_clients} CLIENTS
                {selected.financial_year && <span className="ml-2 text-primary">| {selected.financial_year}</span>}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-bold">Revenue</div>
                <div className="text-lg font-bold">₹{selected.total_revenue.toLocaleString()}</div>
              </div>
              {selected.growth_percentage !== null && (
                <div className="text-right">
                  <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-bold">Growth</div>
                  <div className={`text-lg font-bold flex items-center gap-1 ${
                    selected.growth_percentage > 0 ? 'text-accent' : selected.growth_percentage < 0 ? 'text-destructive' : ''
                  }`}>
                    {selected.growth_percentage > 0 ? <TrendUp size={18} /> : selected.growth_percentage < 0 ? <TrendDown size={18} /> : <Minus size={18} />}
                    {selected.growth_percentage > 0 ? '+' : ''}{selected.growth_percentage}%
                  </div>
                </div>
              )}
              {canWrite && (
                <Button size="sm" className="rounded-sm" onClick={openAdd} data-testid="add-sale-button">
                  <Plus size={16} weight="bold" className="mr-2" />
                  Add Sale
                </Button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-[300px] mb-4">
            <MagnifyingGlass size={16} weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search sales..."
              className="pl-9 rounded-sm"
              data-testid="sales-search-input"
            />
          </div>

          {/* Sales Table */}
          {salesLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading sales...</div>
          ) : filteredSales.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? 'No sales match your search' : 'No sales for this month'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="monthly-sales-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Date</th>
                    <th className="text-left py-2 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Client</th>
                    <th className="text-right py-2 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Amount</th>
                    <th className="text-left py-2 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Description</th>
                    {canWrite && (
                      <th className="text-center py-2 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.slice(0, 50).map((sale) => (
                    <tr key={sale.id} className="border-b border-border" data-testid={`sale-row-${sale.id}`}>
                      <td className="py-3 px-4 text-sm">{sale.date?.slice(0, 10)}</td>
                      <td className="py-3 px-4 font-medium">{sale.client_name}</td>
                      <td className="py-3 px-4 text-right font-semibold">₹{sale.amount.toLocaleString()}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{sale.description || '—'}</td>
                      {canWrite && (
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => openEdit(sale)}
                              className="p-1.5 hover:bg-muted rounded-sm transition-colors"
                              title="Edit"
                              data-testid={`edit-sale-${sale.id}`}
                            >
                              <PencilSimple size={16} weight="regular" />
                            </button>
                            <button
                              onClick={() => setDeleteSale(sale)}
                              className="p-1.5 hover:bg-destructive/10 text-destructive rounded-sm transition-colors"
                              title="Delete"
                              data-testid={`delete-sale-${sale.id}`}
                            >
                              <Trash size={16} weight="regular" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredSales.length > 50 && (
                <div className="text-center py-3 text-sm text-muted-foreground">
                  Showing 50 of {filteredSales.length} sales
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Sale Dialog */}
      <Dialog open={addSaleOpen} onOpenChange={setAddSaleOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl tracking-tight">Add Sale</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSale} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Client *</Label>
              <select
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                className="w-full h-10 px-3 rounded-sm border border-input bg-background"
                required
                data-testid="sale-form-client"
              >
                <option value="">Select a client</option>
                {clients?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Amount *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="rounded-sm"
                required
                data-testid="sale-form-amount"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="rounded-sm"
                required
                data-testid="sale-form-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-sm"
                data-testid="sale-form-description"
              />
            </div>
            <Button type="submit" className="w-full rounded-sm" data-testid="sale-form-submit">Add Sale</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Dialog */}
      <Dialog open={!!editSale} onOpenChange={(open) => { if (!open) setEditSale(null); }}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl tracking-tight">Edit Sale</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSale} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Client *</Label>
              <select
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                className="w-full h-10 px-3 rounded-sm border border-input bg-background"
                required
                data-testid="edit-sale-client"
              >
                <option value="">Select a client</option>
                {clients?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Amount *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="rounded-sm"
                required
                data-testid="edit-sale-amount"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="rounded-sm"
                required
                data-testid="edit-sale-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-sm"
                data-testid="edit-sale-description"
              />
            </div>
            <Button type="submit" className="w-full rounded-sm" data-testid="edit-sale-submit">Update Sale</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Sale Dialog */}
      <Dialog open={!!deleteSale} onOpenChange={(open) => { if (!open) setDeleteSale(null); }}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl tracking-tight">Delete Sale</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Delete this sale of <strong>₹{deleteSale?.amount?.toLocaleString()}</strong> for <strong>{deleteSale?.client_name}</strong> on {deleteSale?.date?.slice(0, 10)}?
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteSale(null)} className="flex-1 rounded-sm">Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteSale} className="flex-1 rounded-sm" data-testid="confirm-delete-sale">Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthlyBreakdown;
